import { useState, useEffect, createContext, useContext, ReactNode } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';

interface SiteAdminData {
  id: string;
  site_id: string;
  role: string;
  first_name: string | null;
  last_name: string | null;
  temp_password_required_reset: boolean;
}

interface SiteData {
  id: string;
  name: string;
  wizard_completed_at: string | null;
  wizard_current_step: string | null;
  can_publish: boolean;
  status: string;
  primary_domain: string | null;
  location_served: string;
  ownership_type: string | null;
}

interface SiteAdminContextType {
  siteAdmin: SiteAdminData | null;
  site: SiteData | null;
  loading: boolean;
  isWizardComplete: boolean;
  canPublish: boolean;
  currentWizardStep: string;
  refetchSite: () => Promise<void>;
  updateWizardStep: (step: string) => Promise<void>;
  completeWizard: () => Promise<void>;
  setCanPublish: (canPublish: boolean) => Promise<void>;
}

const SiteAdminContext = createContext<SiteAdminContextType | undefined>(undefined);

export function SiteAdminProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const [siteAdmin, setSiteAdmin] = useState<SiteAdminData | null>(null);
  const [site, setSite] = useState<SiteData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user) {
      fetchSiteAdminData();
    } else {
      setSiteAdmin(null);
      setSite(null);
      setLoading(false);
    }
  }, [user]);

  const fetchSiteAdminData = async () => {
    if (!user) return;

    try {
      // Check if user is a site admin
      const { data: adminData, error: adminError } = await supabase
        .from('site_admins')
        .select('id, site_id, role, first_name, last_name, temp_password_required_reset')
        .eq('user_id', user.id)
        .maybeSingle();

      if (adminError) throw adminError;

      if (adminData) {
        setSiteAdmin(adminData);

        // Fetch site details
        const { data: siteData, error: siteError } = await supabase
          .from('sites')
          .select('id, name, wizard_completed_at, wizard_current_step, can_publish, status, primary_domain, location_served, ownership_type')
          .eq('id', adminData.site_id)
          .single();

        if (siteError) throw siteError;
        setSite(siteData);
      }
    } catch (error) {
      console.error('Error fetching site admin data:', error);
    } finally {
      setLoading(false);
    }
  };

  const refetchSite = async () => {
    if (siteAdmin?.site_id) {
      const { data: siteData } = await supabase
        .from('sites')
        .select('id, name, wizard_completed_at, wizard_current_step, can_publish, status, primary_domain, location_served, ownership_type')
        .eq('id', siteAdmin.site_id)
        .single();

      if (siteData) {
        setSite(siteData);
      }
    }
  };

  const updateWizardStep = async (step: string) => {
    if (!siteAdmin?.site_id) return;

    await supabase
      .from('sites')
      .update({ wizard_current_step: step })
      .eq('id', siteAdmin.site_id);

    await refetchSite();
  };

  const completeWizard = async () => {
    if (!siteAdmin?.site_id) return;

    await supabase
      .from('sites')
      .update({ 
        wizard_completed_at: new Date().toISOString(),
        wizard_current_step: 'complete'
      })
      .eq('id', siteAdmin.site_id);

    await refetchSite();
  };

  const setCanPublish = async (canPublish: boolean) => {
    if (!siteAdmin?.site_id) return;

    await supabase
      .from('sites')
      .update({ can_publish: canPublish })
      .eq('id', siteAdmin.site_id);

    await refetchSite();
  };

  const isWizardComplete = !!site?.wizard_completed_at;
  const canPublish = !!site?.can_publish;
  const currentWizardStep = site?.wizard_current_step || 'about_us';

  return (
    <SiteAdminContext.Provider
      value={{
        siteAdmin,
        site,
        loading,
        isWizardComplete,
        canPublish,
        currentWizardStep,
        refetchSite,
        updateWizardStep,
        completeWizard,
        setCanPublish,
      }}
    >
      {children}
    </SiteAdminContext.Provider>
  );
}

export function useSiteAdmin() {
  const context = useContext(SiteAdminContext);
  if (context === undefined) {
    throw new Error('useSiteAdmin must be used within a SiteAdminProvider');
  }
  return context;
}
